/**
 * Reglas de markup y pisos de margen.
 *
 * Es la pantalla que saca a un tecnico del camino de una decision comercial:
 * hasta ahora subir el markup del flete medio punto era una migracion y un
 * despliegue.
 *
 * ===========================================================================
 * "GUARDAR" AQUI ES SUSTITUIR
 *
 * El boton dice Guardar y lo que ocurre es que la regla vieja se desactiva y
 * nace una nueva que la sustituye. No es un detalle de implementacion que se
 * pueda esconder: `opcion_linea.regla_id` apunta a la fila y es la unica
 * respuesta a "por que este precio" [Invariante 10], asi que editar en sitio
 * haria mentir a todas las lineas ya cotizadas.
 *
 * La pantalla lo DICE, en el formulario, antes de guardar. Quien cambia un
 * margen tiene que saber que la version anterior sigue existiendo --y que las
 * cotizaciones que la usaron siguen explicandose con ella--, porque es la
 * diferencia entre "he corregido un error" y "he tapado un error".
 *
 * ===========================================================================
 * LA ESPECIFICIDAD, DESGLOSADA Y NO A SECAS
 *
 * "37" no le dice nada a nadie. "37 = cliente (32) + cargo (4) + modo (1)" dice
 * exactamente por que esta regla gana a la de al lado. Los pesos llegan del
 * servidor --son los del motor-- y aqui solo se suman para explicar; el numero
 * que se ensena es el que calculo el servidor, no uno recalculado aqui.
 *
 * La lista va ordenada de mas especifica a menos, que es el orden en que el
 * motor las mira: la primera que aplique a una linea concreta es la que manda.
 */

import type {
  Margenes,
  PesosCascada,
  PisoMargenVista,
  ReglaMargen,
  Tercero,
} from '../api';
import { el } from '../dom';
import { formatearFecha, formatearFraccionPorcentual, type Idioma } from '../formato';
import type { Traductor } from '../i18n';

/** Lo que hay abierto: nada, el formulario de una regla, o el de un piso. Al
 *  sustituir, `id` es la que se reemplaza; al crear, `null`. */
export interface EstadoFormMargen {
  readonly que: 'regla' | 'piso';
  readonly id: string | null;
  readonly descripcion: string;
  readonly tipo: string;
  readonly valor: string;
  readonly modo: string;
  readonly tramo: string;
  readonly codigoCargo: string;
  readonly trade: string;
  readonly clienteId: string;
  readonly vigenciaDesde: string;
  readonly vigenciaHasta: string;
  readonly margenMinPct: string;
  readonly margenMinAbs: string;
}

export const REGLA_VACIA: EstadoFormMargen = {
  que: 'regla',
  id: null,
  descripcion: '',
  // PORCENTAJE es el unico tipo que usan las tres reglas sembradas y el que se
  // entiende sin explicacion. Los otros cuatro estan en el desplegable.
  tipo: 'PORCENTAJE',
  valor: '',
  modo: '',
  tramo: '',
  codigoCargo: '',
  trade: '',
  clienteId: '',
  vigenciaDesde: '',
  vigenciaHasta: '',
  margenMinPct: '',
  margenMinAbs: '',
};

export const PISO_VACIO: EstadoFormMargen = { ...REGLA_VACIA, que: 'piso' };

const TIPOS = [
  'PORCENTAJE',
  'FIJO_UNIDAD',
  'VENTA_FIJA',
  'MARGEN_OBJETIVO',
  'SIN_MARGEN',
] as const;

const MODOS = ['FCL', 'LCL', 'AEREO'] as const;
const TRAMOS = ['ORIGEN', 'FLETE', 'DESTINO', 'OTRO'] as const;

export interface OpcionesMargenes {
  readonly t: Traductor;
  readonly idioma: Idioma;
  readonly datos: Margenes | null;
  readonly clientes: readonly Tercero[];
  readonly cargando: boolean;
  readonly trabajando: boolean;
  readonly todas: boolean;
  readonly form: EstadoFormMargen | null;
  readonly faltan: readonly string[];
  readonly aviso: string | null;
  readonly onVerTodas: (todas: boolean) => void;
  readonly onAbrirForm: (form: EstadoFormMargen | null) => void;
  readonly onCambioForm: (form: EstadoFormMargen) => void;
  readonly onGuardar: () => void;
  readonly onEstado: (que: 'regla' | 'piso', id: string, activa: boolean) => void;
}

export function pintarMargenes(o: OpcionesMargenes): HTMLElement {
  const { t } = o;

  if (o.form !== null) return formulario(o, o.form);

  return el('div', { clase: 'zona-margenes' }, [
    el('header', { clase: 'zona-cabecera' }, [
      el('h2', { texto: t('margenes.titulo') }),
      el('p', { clase: 'tenue', texto: t('margenes.intro') }),
    ]),

    o.aviso === null ? null : el('p', { clase: 'aviso aviso-ok', texto: o.aviso }),

    el('div', { clase: 'rfq-acciones' }, [
      el('button', {
        clase: `boton boton-plano ${o.todas ? '' : 'boton-activo'}`,
        texto: t('margenes.verActivas'),
        attrs: { type: 'button' },
        onClick: () => {
          o.onVerTodas(false);
        },
      }),
      el('button', {
        clase: `boton boton-plano ${o.todas ? 'boton-activo' : ''}`,
        texto: t('margenes.verTodas'),
        attrs: { type: 'button' },
        onClick: () => {
          o.onVerTodas(true);
        },
      }),
    ]),

    o.cargando || o.datos === null
      ? el('p', { clase: 'tenue', texto: t('app.cargando') })
      : el('div', {}, [seccionReglas(o, o.datos), seccionPisos(o, o.datos)]),
  ]);
}

// ---------------------------------------------------------------------------
// Reglas
// ---------------------------------------------------------------------------

function seccionReglas(o: OpcionesMargenes, d: Margenes): HTMLElement {
  const { t } = o;

  // De mas especifica a menos: es el orden en que el motor las mira, y leerla
  // al reves obliga a reconstruir la cascada de cabeza.
  const reglas = [...d.reglas].sort((a, b) => b.especificidad - a.especificidad);

  return el('section', { clase: 'panel panel-margenes' }, [
    el('header', { clase: 'zona-cabecera' }, [
      el('h3', { texto: t('margenes.reglas.titulo') }),
      el('p', { clase: 'tenue', texto: t('margenes.reglas.explicacion') }),
    ]),

    el('button', {
      clase: 'boton boton-primario',
      texto: t('margenes.reglas.nueva'),
      attrs: { type: 'button' },
      onClick: () => {
        o.onAbrirForm(REGLA_VACIA);
      },
    }),

    reglas.length === 0
      ? el('p', { clase: 'tenue', texto: t('margenes.reglas.vacia') })
      : el('table', { clase: 'tabla-resultados tabla-margenes' }, [
          el('thead', {}, [
            el('tr', {}, [
              el('th', { texto: t('margenes.col.descripcion') }),
              el('th', { texto: t('margenes.col.aplicaA') }),
              el('th', { clase: 'celda-numero', texto: t('margenes.col.especificidad') }),
              el('th', { texto: t('margenes.col.tipo') }),
              el('th', { clase: 'celda-monto', texto: t('margenes.col.valor') }),
              el('th', { texto: t('margenes.col.cambio') }),
              el('th', {}),
            ]),
          ]),
          el(
            'tbody',
            {},
            reglas.map((r) => filaRegla(o, d.pesos, r)),
          ),
        ]),
  ]);
}

function filaRegla(
  o: OpcionesMargenes,
  pesos: PesosCascada,
  r: ReglaMargen,
): HTMLElement {
  const { t } = o;

  return el('tr', { clase: r.activa ? undefined : 'fila-bloqueada' }, [
    el('td', {}, [
      el('span', { texto: r.descripcion }),
      r.activa ? null : el('span', { clase: 'badge', texto: t('margenes.inactiva') }),
      r.sustituye_a === null
        ? null
        : // Que esta regla nacio reemplazando a otra. Es media cadena de
          // cambios y la otra media es `desactivada_por` de la anterior.
          el('span', { clase: 'badge', texto: t('margenes.sustituye') }),
    ]),
    el('td', {}, [dimensiones(o, r)]),
    el('td', {
      clase: 'celda-numero',
      texto: String(r.especificidad),
      // El desglose entero en el titulo: "37 = cliente (32) + cargo (4) + modo
      // (1)". Un numero suelto no dice por que esta regla gana a la de al lado.
      attrs: { title: desglose(o.t, pesos, r) },
    }),
    el('td', { texto: t(`margenes.tipo.${r.tipo}`) }),
    el('td', { clase: 'celda-monto', texto: valorLegible(o, r) }),
    el('td', { clase: 'tenue' }, [
      el('span', {
        texto: `${formatearFecha(r.creada_en.slice(0, 10), o.idioma)} · ${
          r.creada_por ?? t('margenes.porMigracion')
        }`,
      }),
      r.desactivada_en === null
        ? null
        : el('span', {
            clase: 'tenue',
            texto: ` → ${formatearFecha(r.desactivada_en.slice(0, 10), o.idioma)} · ${
              r.desactivada_por ?? t('margenes.porMigracion')
            }`,
          }),
    ]),
    el('td', {}, [
      r.activa
        ? el('button', {
            clase: 'boton boton-plano boton-abrir',
            texto: t('margenes.editar'),
            attrs: o.trabajando
              ? { type: 'button', disabled: 'disabled' }
              : { type: 'button' },
            onClick: () => {
              o.onAbrirForm(deRegla(r));
            },
          })
        : null,
      el('button', {
        clase: 'boton boton-plano',
        texto: r.activa ? t('margenes.desactivar') : t('margenes.reactivar'),
        attrs: o.trabajando
          ? { type: 'button', disabled: 'disabled' }
          : { type: 'button' },
        onClick: () => {
          o.onEstado('regla', r.id, !r.activa);
        },
      }),
    ]),
  ]);
}

/** Las dimensiones que acotan la regla, o "todas" si no acota ninguna. */
function dimensiones(o: OpcionesMargenes, r: ReglaMargen): HTMLElement {
  const { t } = o;
  const partes: string[] = [];
  if (r.cliente !== null)
    partes.push(`${t('margenes.dim.cliente')}: ${r.cliente.nombre}`);
  if (r.pol !== null && r.pod !== null) {
    partes.push(`${r.pol.nombre} → ${r.pod.nombre}`);
  }
  if (r.trade !== null) partes.push(`${t('margenes.dim.trade')}: ${r.trade}`);
  if (r.codigo_cargo !== null) partes.push(r.codigo_cargo);
  if (r.tramo !== null) partes.push(t(`tramo.${r.tramo}`));
  if (r.modo !== null) partes.push(t(`modo.${r.modo}`));

  return partes.length === 0
    ? // La regla global. Se dice con palabras y no con una celda vacia: una
      // celda vacia se lee como "no lo sabemos".
      el('span', { clase: 'tenue', texto: t('margenes.aplicaATodo') })
    : el('span', { texto: partes.join(' · ') });
}

function desglose(t: Traductor, pesos: PesosCascada, r: ReglaMargen): string {
  const partes: string[] = [];
  if (r.cliente !== null)
    partes.push(`${t('margenes.dim.cliente')} (${pesos.clienteId})`);
  if (r.pol !== null && r.pod !== null)
    partes.push(`${t('margenes.dim.ruta')} (${pesos.polPod})`);
  if (r.trade !== null) partes.push(`${t('margenes.dim.trade')} (${pesos.trade})`);
  if (r.codigo_cargo !== null)
    partes.push(`${t('margenes.dim.cargo')} (${pesos.codigoCargo})`);
  if (r.tramo !== null) partes.push(`${t('margenes.dim.tramo')} (${pesos.tramo})`);
  if (r.modo !== null) partes.push(`${t('margenes.dim.modo')} (${pesos.modo})`);

  return partes.length === 0
    ? t('margenes.desgloseGlobal')
    : `${String(r.especificidad)} = ${partes.join(' + ')}`;
}

/**
 * El valor, en la unidad que le corresponde a su tipo.
 *
 * PORCENTAJE y MARGEN_OBJETIVO son fracciones y se ensenan como porcentaje;
 * FIJO_UNIDAD y VENTA_FIJA son dinero por unidad. Ensenar "0.0800" en los
 * cuatro casos obligaria a saberse de memoria cual es cual.
 *
 * No se opera con el numero: `formatearFraccionPorcentual` hace el x100 con
 * `decimal.js` sobre la cadena, porque en punto flotante 0.07 x 100 da
 * 7.000000000000001 [formato.ts].
 */
function valorLegible(o: OpcionesMargenes, r: ReglaMargen): string {
  if (r.tipo === 'SIN_MARGEN') return '—';
  if (r.tipo === 'PORCENTAJE' || r.tipo === 'MARGEN_OBJETIVO') {
    return formatearFraccionPorcentual(r.valor, o.idioma);
  }
  return `${r.valor} / ${o.t('margenes.porUnidad')}`;
}

function deRegla(r: ReglaMargen): EstadoFormMargen {
  return {
    ...REGLA_VACIA,
    que: 'regla',
    id: r.id,
    descripcion: r.descripcion,
    tipo: r.tipo,
    valor: r.valor,
    modo: r.modo ?? '',
    tramo: r.tramo ?? '',
    codigoCargo: r.codigo_cargo ?? '',
    trade: r.trade ?? '',
    clienteId: r.cliente?.id ?? '',
    vigenciaDesde: r.vigencia_desde ?? '',
    vigenciaHasta: r.vigencia_hasta ?? '',
  };
}

// ---------------------------------------------------------------------------
// Pisos
// ---------------------------------------------------------------------------

function seccionPisos(o: OpcionesMargenes, d: Margenes): HTMLElement {
  const { t } = o;

  return el('section', { clase: 'panel panel-margenes' }, [
    el('header', { clase: 'zona-cabecera' }, [
      el('h3', { texto: t('margenes.pisos.titulo') }),
      el('p', { clase: 'tenue', texto: t('margenes.pisos.explicacion') }),
    ]),

    el('button', {
      clase: 'boton boton-primario',
      texto: t('margenes.pisos.nuevo'),
      attrs: { type: 'button' },
      onClick: () => {
        o.onAbrirForm(PISO_VACIO);
      },
    }),

    d.pisos.length === 0
      ? el('p', { clase: 'tenue', texto: t('margenes.pisos.vacio') })
      : el('table', { clase: 'tabla-resultados tabla-margenes' }, [
          el('thead', {}, [
            el('tr', {}, [
              el('th', { texto: t('margenes.col.descripcion') }),
              el('th', { texto: t('margenes.col.aplicaA') }),
              el('th', { clase: 'celda-monto', texto: t('margenes.col.minimo') }),
              el('th', { texto: t('margenes.col.cambio') }),
              el('th', {}),
            ]),
          ]),
          el(
            'tbody',
            {},
            d.pisos.map((p) => filaPiso(o, p)),
          ),
        ]),
  ]);
}

function filaPiso(o: OpcionesMargenes, p: PisoMargenVista): HTMLElement {
  const { t } = o;
  const partes: string[] = [];
  if (p.cliente !== null)
    partes.push(`${t('margenes.dim.cliente')}: ${p.cliente.nombre}`);
  if (p.trade !== null) partes.push(`${t('margenes.dim.trade')}: ${p.trade}`);
  if (p.modo !== null) partes.push(t(`modo.${p.modo}`));

  return el('tr', { clase: p.activa ? undefined : 'fila-bloqueada' }, [
    el('td', {}, [
      el('span', { texto: p.descripcion }),
      p.activa ? null : el('span', { clase: 'badge', texto: t('margenes.inactiva') }),
      p.sustituye_a === null
        ? null
        : el('span', { clase: 'badge', texto: t('margenes.sustituye') }),
    ]),
    el('td', {}, [
      partes.length === 0
        ? el('span', { clase: 'tenue', texto: t('margenes.aplicaATodo') })
        : el('span', { texto: partes.join(' · ') }),
    ]),
    el('td', { clase: 'celda-monto' }, [
      p.margen_min_pct === null
        ? null
        : el('span', { texto: formatearFraccionPorcentual(p.margen_min_pct, o.idioma) }),
      p.margen_min_abs === null
        ? null
        : el('span', { clase: 'tenue', texto: ` · ${p.margen_min_abs}` }),
    ]),
    el('td', {
      clase: 'tenue',
      texto: `${formatearFecha(p.creada_en.slice(0, 10), o.idioma)} · ${
        p.creada_por ?? t('margenes.porMigracion')
      }`,
    }),
    el('td', {}, [
      p.activa
        ? el('button', {
            clase: 'boton boton-plano boton-abrir',
            texto: t('margenes.editar'),
            attrs: o.trabajando
              ? { type: 'button', disabled: 'disabled' }
              : { type: 'button' },
            onClick: () => {
              o.onAbrirForm({
                ...PISO_VACIO,
                id: p.id,
                descripcion: p.descripcion,
                modo: p.modo ?? '',
                trade: p.trade ?? '',
                clienteId: p.cliente?.id ?? '',
                margenMinPct: p.margen_min_pct ?? '',
                margenMinAbs: p.margen_min_abs ?? '',
              });
            },
          })
        : null,
      el('button', {
        clase: 'boton boton-plano',
        texto: p.activa ? t('margenes.desactivar') : t('margenes.reactivar'),
        attrs: o.trabajando
          ? { type: 'button', disabled: 'disabled' }
          : { type: 'button' },
        onClick: () => {
          o.onEstado('piso', p.id, !p.activa);
        },
      }),
    ]),
  ]);
}

// ---------------------------------------------------------------------------
// El formulario
// ---------------------------------------------------------------------------

function campo(
  id: string,
  etiqueta: string,
  control: HTMLElement,
  ayuda?: string,
  falta = false,
): HTMLElement {
  return el('div', { clase: `campo ${falta ? 'campo-falta' : ''}` }, [
    el('label', { clase: 'etiqueta', texto: etiqueta, attrs: { for: id } }),
    control,
    ayuda === undefined ? null : el('p', { clase: 'ayuda tenue', texto: ayuda }),
  ]);
}

function entrada(
  id: string,
  tipo: string,
  valor: string,
  onCambio: (v: string) => void,
  extra: Record<string, string> = {},
): HTMLInputElement {
  const nodo = el('input', {
    clase: 'control',
    attrs: { id, type: tipo, value: valor, ...extra },
  });
  nodo.addEventListener('input', () => {
    onCambio(nodo.value);
  });
  return nodo;
}

function desplegable(
  id: string,
  valor: string,
  opciones: readonly { valor: string; texto: string }[],
  onCambio: (v: string) => void,
): HTMLSelectElement {
  const nodo = el('select', { clase: 'control', attrs: { id } });
  for (const op of opciones) {
    nodo.appendChild(el('option', { texto: op.texto, attrs: { value: op.valor } }));
  }
  nodo.value = valor;
  nodo.addEventListener('change', () => {
    onCambio(nodo.value);
  });
  return nodo;
}

function formulario(o: OpcionesMargenes, f: EstadoFormMargen): HTMLElement {
  const { t } = o;
  const esRegla = f.que === 'regla';
  const sustituye = f.id !== null;
  const cambio = (v: Partial<EstadoFormMargen>): void => {
    o.onCambioForm({ ...f, ...v });
  };
  const falta = (campoId: string): boolean => o.faltan.includes(campoId);

  /** "Cualquiera" como primera opcion de cada dimension. La cadena vacia es lo
   *  que la API lee como "sin acotar": ver `dimension()` en margenes.ts. */
  const cualquiera = { valor: '', texto: t('margenes.cualquiera') };

  const form = el('form', { clase: 'margenes-form' }, [
    campo(
      'margen-descripcion',
      t('margenes.campo.descripcion'),
      entrada(
        'margen-descripcion',
        'text',
        f.descripcion,
        (v) => {
          cambio({ descripcion: v });
        },
        { maxlength: '300' },
      ),
      t(esRegla ? 'margenes.ayuda.descripcionRegla' : 'margenes.ayuda.descripcionPiso'),
      falta('descripcion'),
    ),

    esRegla
      ? campo(
          'margen-tipo',
          t('margenes.campo.tipo'),
          desplegable(
            'margen-tipo',
            f.tipo,
            TIPOS.map((x) => ({ valor: x, texto: t(`margenes.tipo.${x}`) })),
            (v) => {
              cambio({ tipo: v });
            },
          ),
          t(`margenes.ayudaTipo.${f.tipo}`),
        )
      : null,

    esRegla && f.tipo !== 'SIN_MARGEN'
      ? campo(
          'margen-valor',
          t('margenes.campo.valor'),
          entrada(
            'margen-valor',
            'text',
            f.valor,
            (v) => {
              cambio({ valor: v });
            },
            { inputmode: 'decimal' },
          ),
          // El dedazo mas probable de esta pantalla: escribir 8 donde va 0.08.
          // La API tambien lo rechaza; esto lo dice antes.
          t(
            f.tipo === 'PORCENTAJE' || f.tipo === 'MARGEN_OBJETIVO'
              ? 'margenes.ayuda.valorFraccion'
              : 'margenes.ayuda.valorMonto',
          ),
          falta('valor'),
        )
      : null,

    esRegla
      ? null
      : el('div', {}, [
          campo(
            'piso-pct',
            t('margenes.campo.minPct'),
            entrada(
              'piso-pct',
              'text',
              f.margenMinPct,
              (v) => {
                cambio({ margenMinPct: v });
              },
              { inputmode: 'decimal' },
            ),
            t('margenes.ayuda.minPct'),
            falta('minimo'),
          ),
          campo(
            'piso-abs',
            t('margenes.campo.minAbs'),
            entrada(
              'piso-abs',
              'text',
              f.margenMinAbs,
              (v) => {
                cambio({ margenMinAbs: v });
              },
              { inputmode: 'decimal' },
            ),
            t('margenes.ayuda.minAbs'),
          ),
        ]),

    el('h4', { texto: t('margenes.dondeAplica') }),
    el('p', { clase: 'tenue', texto: t('margenes.dondeAplicaAyuda') }),

    campo(
      'margen-modo',
      t('margenes.campo.modo'),
      desplegable(
        'margen-modo',
        f.modo,
        [cualquiera, ...MODOS.map((m) => ({ valor: m, texto: t(`modo.${m}`) }))],
        (v) => {
          cambio({ modo: v });
        },
      ),
    ),

    esRegla
      ? campo(
          'margen-tramo',
          t('margenes.campo.tramo'),
          desplegable(
            'margen-tramo',
            f.tramo,
            [cualquiera, ...TRAMOS.map((x) => ({ valor: x, texto: t(`tramo.${x}`) }))],
            (v) => {
              cambio({ tramo: v });
            },
          ),
        )
      : null,

    esRegla
      ? campo(
          'margen-cargo',
          t('margenes.campo.codigoCargo'),
          entrada(
            'margen-cargo',
            'text',
            f.codigoCargo,
            (v) => {
              cambio({ codigoCargo: v.toUpperCase() });
            },
            { autocapitalize: 'characters', spellcheck: 'false' },
          ),
          // [CLAUDE.md] No inventar codigos: si no existe, la base lo rechaza
          // por la clave foranea. Se dice antes de que ocurra.
          t('margenes.ayuda.codigoCargo'),
        )
      : null,

    campo(
      'margen-cliente',
      t('margenes.campo.cliente'),
      desplegable(
        'margen-cliente',
        f.clienteId,
        [cualquiera, ...o.clientes.map((c) => ({ valor: c.id, texto: c.nombre }))],
        (v) => {
          cambio({ clienteId: v });
        },
      ),
      t('margenes.ayuda.cliente'),
    ),

    esRegla
      ? campo(
          'margen-desde',
          t('margenes.campo.vigenciaDesde'),
          entrada('margen-desde', 'date', f.vigenciaDesde, (v) => {
            cambio({ vigenciaDesde: v });
          }),
          t('margenes.ayuda.vigencia'),
        )
      : null,

    esRegla
      ? campo(
          'margen-hasta',
          t('margenes.campo.vigenciaHasta'),
          entrada('margen-hasta', 'date', f.vigenciaHasta, (v) => {
            cambio({ vigenciaHasta: v });
          }),
        )
      : null,

    // Lo que va a pasar de verdad al pulsar Guardar. Va JUNTO al boton y no en
    // una nota al pie: quien cambia un margen tiene que saber que la version
    // anterior sigue existiendo y que las cotizaciones que la usaron siguen
    // explicandose con ella.
    sustituye
      ? el('div', { clase: 'aviso aviso-neutro' }, [
          el('strong', { texto: t('margenes.sustitucion.titulo') }),
          el('p', { texto: t('margenes.sustitucion.explicacion') }),
        ])
      : null,

    el('div', { clase: 'rfq-acciones' }, [
      el('button', {
        clase: 'boton boton-primario',
        texto: sustituye ? t('margenes.guardarSustituyendo') : t('margenes.crear'),
        attrs: o.trabajando
          ? { type: 'submit', disabled: 'disabled' }
          : { type: 'submit' },
      }),
      el('button', {
        clase: 'boton boton-plano',
        texto: t('margenes.cancelar'),
        attrs: { type: 'button' },
        onClick: () => {
          o.onAbrirForm(null);
        },
      }),
    ]),
  ]);

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    if (!o.trabajando) o.onGuardar();
  });

  return el('section', { clase: 'panel panel-margenes' }, [
    el('h2', {
      texto: t(
        sustituye
          ? esRegla
            ? 'margenes.reglas.editar'
            : 'margenes.pisos.editar'
          : esRegla
            ? 'margenes.reglas.nueva'
            : 'margenes.pisos.nuevo',
      ),
    }),
    form,
  ]);
}
